import { useEffect } from 'react'
import { GA4_CONFIG } from '../../config/analytics'

const GoogleAnalytics = ({ measurementId }) => {
  useEffect(() => {
    // Only load GA4 if measurement ID is provided and we're in browser
    if (!measurementId || typeof window === 'undefined') return

    // Load Google Analytics 4 script
    const script = document.createElement('script')
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    script.async = true
    document.head.appendChild(script)

    // Initialize gtag
    window.dataLayer = window.dataLayer || []
    function gtag() {
      window.dataLayer.push(arguments)
    }
    window.gtag = gtag

    gtag('js', new Date())
    gtag('config', measurementId, {
      page_title: document.title,
      page_location: window.location.href,
      debug_mode: GA4_CONFIG.SETTINGS.DEBUG_MODE
    })

    // Store measurement ID globally for other functions
    window.GA_MEASUREMENT_ID = measurementId

    if (GA4_CONFIG.SETTINGS.DEBUG_MODE) {
      console.log('Google Analytics 4 initialized with ID:', measurementId)
    }

    // Cleanup function
    return () => {
      // Remove the script if component unmounts
      const existingScript = document.querySelector(`script[src*="${measurementId}"]`)
      if (existingScript) {
        existingScript.remove()
      }
    }
  }, [measurementId])

  return null // This component doesn't render anything
}

// Utility functions for tracking events
export const trackEvent = (eventName, parameters = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, parameters)
    if (GA4_CONFIG.SETTINGS.DEBUG_MODE) {
      console.log('GA4 Event tracked:', eventName, parameters)
    }
  }
}

export const trackPageView = (pagePath, pageTitle) => {
  if (typeof window !== 'undefined' && window.gtag && window.GA_MEASUREMENT_ID) {
    window.gtag('config', window.GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle
    })
    if (GA4_CONFIG.SETTINGS.DEBUG_MODE) {
      console.log('GA4 Page view tracked:', pagePath, pageTitle)
    }
  }
}

// Common event tracking functions
export const trackIconGeneration = (iconType, hasBackground = false, hasBadge = false) => {
  trackEvent('icon_generated', {
    icon_type: iconType,
    has_background: hasBackground,
    has_badge: hasBadge,
    event_category: 'engagement',
    event_label: 'icon_creation'
  })
}

export const trackImageUpload = (fileSize, fileType) => {
  trackEvent('image_uploaded', {
    file_size: fileSize,
    file_type: fileType,
    event_category: 'engagement',
    event_label: 'file_upload'
  })
}

export const trackDownload = (downloadType) => {
  trackEvent('download_started', {
    download_type: downloadType,
    event_category: 'conversion',
    event_label: 'icon_download'
  })
}

export const trackColorChange = (colorType, colorValue) => {
  trackEvent('color_changed', {
    color_type: colorType,
    color_value: colorValue,
    event_category: 'engagement',
    event_label: 'customization'
  })
}

export const trackDonationClick = () => {
  trackEvent('donation_clicked', {
    event_category: 'conversion',
    event_label: 'support'
  })
}

export const trackEmailClick = () => {
  trackEvent('email_clicked', {
    event_category: 'engagement',
    event_label: 'contact'
  })
}

export default GoogleAnalytics 